<template>
  <v-container v-if="isAdministrator">
    <v-row>
      <v-col cols="8">
        <v-text-field
          :label="$t('usersView.searchTerm')"
          v-model="query"
          append-icon="mdi-magnify"
          outlined
          clearable
        >
        </v-text-field>
      </v-col>
      <v-col cols="3">
        <v-select
          :items="groupValues"
          v-model="selectedGroup"
          :label="$t('usersView.group')"
          outlined
        >
        </v-select>
      </v-col>
      <v-col cols="1">
        <v-btn
          class="mt-1"
          tile
          large
          color="primary"
          icon
          @click="showUserAddForm()"
        >
          <v-icon>mdi-account-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-n8" v-if="selectedGroup === 'member'">
      <v-col>
        <v-autocomplete
          v-model="filterYear"
          :items="years"
          :label="$t('form.fields.year')"
          outlined
        ></v-autocomplete>
      </v-col>
      <v-col>
        <v-select
          v-model="filterPaymentState"
          :items="paymentStates"
          :label="$t('form.fields.paymentState')"
          outlined
          clearable
        ></v-select>
      </v-col>
      <v-col cols="1">
        <v-btn
          class="mt-1"
          tile
          large
          color="primary"
          icon
          :disabled="selectedUsers.length === 0"
          @click="switchPaymentStateDialog = true"
        >
          <v-icon>mdi-account-cash</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-n8">
      <v-col>
        <v-data-table
          v-model="selectedUsers"
          :headers="headers"
          :items="filteredUsers"
          :search="query"
          :loading="loading"
          :footer-props="dataTableFooterProps"
          :options="{
            sortBy: ['createdTimestamp'],
            sortDesc: [true],
          }"
          :show-select="selectedGroup === 'member'"
        >
          <template #[`footer.page-text`]="{ pageStart,pageStop,itemsLength }">
            {{ $t("pageText", { pageStart, pageStop, itemsLength }) }}
          </template>
          <template #[`item.createdTimestamp`]="{ item }">
            {{ item.createdTimestamp | formatDateTime }}
          </template>
          <template #[`item.paymentState`]="{ item }">
            <span
              :class="
                `${
                  getPaymentStateForUser(item) === 'paid' ? 'green' : 'red'
                }--text`
              "
            >
              {{ $t(`paymentState.${getPaymentStateForUser(item)}`) }}
            </span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              tile
              large
              color="primary"
              icon
              @click="showTransferUserForm(item)"
              v-if="selectedGroup === 'unassigned'"
            >
              <v-icon>mdi-account-switch</v-icon>
            </v-btn>
            <v-btn
              tile
              large
              color="primary"
              icon
              @click="switchSinglePaymentState(item)"
              v-if="selectedGroup === 'member'"
            >
              <v-icon>mdi-account-cash</v-icon>
            </v-btn>
            <v-btn
              tile
              large
              color="primary"
              icon
              @click="showUserEditForm(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              tile
              large
              color="primary"
              icon
              :disabled="item.id === currentUser.id"
              @click="showUserDeleteConfirmationForm(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog v-model="switchPaymentStateDialog" max-width="550px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("usersView.switchPaymentState") }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="switchPaymentStateDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="8">
              <v-select
                v-model="switchPaymentState"
                :items="paymentStates"
                :label="$t('form.fields.paymentState')"
                outlined
              ></v-select>
            </v-col>
            <v-col>
              <v-btn
                class="mt-2"
                color="primary"
                :disabled="!switchPaymentState"
                @click="doSwitchPaymentState"
                :loading="switchingState"
              >
                {{ $t("form.btn.save") }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="switchPaymentState" class="mt-n8 mb-n4">
            <v-col>
              <v-alert
                dense
                outlined
                type="info"
                v-html="
                  $t('usersView.switchPaymentStateText', {
                    count: selectedUsers.length,
                    state: $t(`paymentState.${switchPaymentState}`),
                    year: filterYear,
                  })
                "
              >
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editUserDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("usersView.editUser") }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeUserEditForm(true)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <user-data-form
            :user="editUser"
            @userSave="closeUserEditForm(false)"
          ></user-data-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="transferUserDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("usersView.transferUser") }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="transferUserDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="editUser">
          <v-row
            class="mb-n8"
            v-if="
              editUser.attributes &&
                editUser.attributes.memberType &&
                editUser.attributes.societySections
            "
          >
            <v-col v-if="editUser.attributes.memberType">
              <v-text-field
                :label="$t('form.fields.membership')"
                :value="$t(`memberType.${editUser.attributes.memberType}`)"
                outlined
                disabled
              ></v-text-field>
            </v-col>
            <v-col v-if="editUser.attributes.societySections">
              <v-textarea
                :label="$t('form.fields.societySections')"
                :value="
                  editUser.attributes.societySections
                    .map((section) => $t(`societySection.${section}`))
                    .join(', ')
                "
                :rows="1"
                auto-grow
                outlined
                disabled
              >
              </v-textarea>
            </v-col>
          </v-row>
          <v-row class="mb-n4">
            <v-col cols="8">
              <v-text-field
                :label="$t('form.fields.membershipNumber') + ' *'"
                v-model="newMembershipNumber"
                required
                outlined
              >
              </v-text-field>
            </v-col>
            <v-col>
              <v-btn
                class="mt-2"
                color="primary"
                @click="transferUserData()"
                :loading="transferring"
                :disabled="newMembershipNumber == null"
              >
                {{ $t("form.btn.activate") }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="mt-n8 mb-n4">
            <v-col>
              <v-alert
                v-if="transferErrorMessage"
                dense
                outlined
                type="error"
                v-html="transferErrorMessage"
              >
              </v-alert>
              <v-alert
                v-else
                dense
                outlined
                type="info"
                v-html="
                  $t('usersView.transferUserText', {
                    mail: editUser.email,
                  })
                "
              >
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="addUserDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("usersView.createUser") }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeUserAddForm(false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-tabs :value="0" grow fixed centered>
            <v-tab>
              <v-icon>mdi-account-plus</v-icon>
              &nbsp;{{ $t("usersView.enterData") }}
            </v-tab>
            <v-tab>
              <v-icon>mdi-upload</v-icon>
              &nbsp;{{ $t("usersView.importData") }}
            </v-tab>
            <v-tab-item class="mt-3">
              <user-data-form
                :user="editUser"
                :createUser="true"
                @userSave="closeUserAddForm(true)"
              ></user-data-form>
            </v-tab-item>
            <v-tab-item class="mt-3">
              <v-row>
                <v-col>
                  <v-file-input
                    v-model="importFile"
                    accept="application/xml"
                    label="XML*"
                    outlined
                    prepend-icon="mdi-paperclip"
                    :loading="importing"
                  ></v-file-input>
                </v-col>
              </v-row>
              <v-row class="mt-n8">
                <v-col>
                  <v-combobox
                    v-model="importGroups"
                    :items="selectionGroups"
                    :label="$t('form.fields.groups')"
                    multiple
                    outlined
                    :hint="$t('usersView.importGroupHint')"
                    persistent-hint
                  >
                    <template v-slot:item="{ item }">
                      {{ $t(`groups.${item}`) }}
                    </template>
                    <template v-slot:selection="{ attrs, item, selected }">
                      <v-chip
                        v-bind="attrs"
                        :input-value="selected"
                        close="true"
                        @click:close="removeImportGroup(item)"
                      >
                        {{ $t(`groups.${item}`) }}
                      </v-chip>
                    </template>
                  </v-combobox>
                </v-col>
              </v-row>
              <v-row class="mt-n8">
                <v-col>
                  <v-switch
                    v-model="sendImportMails"
                    :label="$t('usersView.sendImportMails')"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-card-actions class="mt-3">
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  @click="importUserData()"
                  :loading="importing"
                  :disabled="importFile == null"
                >
                  {{ $t("usersView.importData") }}
                </v-btn>
              </v-card-actions>
            </v-tab-item>
          </v-tabs>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmImportDialog" persistent max-width="300px">
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("usersView.importSuccessful") }}
        </v-card-title>
        <v-card-text
          class="title pt-5"
          v-html="
            $t('usersView.importSuccessfulText', {
              created: importResult.created,
              updated: importResult.updated,
            })
          "
        >
        </v-card-text>
        <v-alert
          dense
          outlined
          type="error"
          v-if="importResult.duplicateMails"
          v-html="$t('usersView.importErrorDuplicateMail')"
        >
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeImportConfirmationDialog()">
            {{ $t("form.btn.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteUserDialog" persistent max-width="300px">
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("usersView.deleteUser") }}
        </v-card-title>
        <v-card-text
          class="title pt-5"
          v-html="
            $t('usersView.deleteUserText', {
              username: editUser != null ? editUser.username : null,
            })
          "
        >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="deleteUser()">
            {{ $t("yes") }}
          </v-btn>
          <v-btn color="primary" text @click="deleteUserDialog = false">
            {{ $t("no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import {
  usersGroupsResource,
  usersResource,
  usersImportResource,
  switchPaymentStateResource,
} from "../api/resources";
import {
  AllUserGroups,
  PaymentStates,
  SelectionUserGroups,
} from "../config/constants";
import UserDataForm from "./UserDataForm";
import * as moment from "moment";

export default {
  components: { UserDataForm },
  data: () => ({
    query: null,
    loading: false,
    editUserDialog: false,
    addUserDialog: false,
    deleteUserDialog: false,
    confirmImportDialog: false,
    transferUserDialog: false,
    switchPaymentStateDialog: false,
    importing: false,
    editUser: null,
    oldUser: null,
    importFile: null,
    sendImportMails: false,
    filterYear: moment().year(),
    filterPaymentState: null,
    switchPaymentState: null,
    switchingState: false,
    newMembershipNumber: null,
    transferErrorMessage: null,
    transferring: false,
    selectedUsers: [],
    users: [],
    selectedGroup: "unassigned",
    importGroups: [],
    importResult: {},
    groups: AllUserGroups,
    selectionGroups: SelectionUserGroups,
  }),
  computed: {
    ...mapGetters([
      "isAdministrator",
      "currentUser",
      "pageSettings",
      "dataTableFooterProps",
    ]),
    groupValues() {
      return this.groups.map((group) => ({
        value: group,
        text: this.$t(`groups.${group}`),
      }));
    },
    headers() {
      const headers = [
        { text: this.$t("usersView.created"), value: "createdTimestamp" },
        {
          text: this.$t(
            `form.fields.${
              this.selectedGroup === "member" ? "membershipNumber" : "username"
            }`
          ),
          value: "username",
        },
        {
          text: this.$t("form.fields.name"),
          value: "fullName",
        },
      ];
      if (this.selectedGroup === "member") {
        headers.push({
          text: `${this.$t("form.fields.paymentState")} (${this.filterYear})`,
          value: "paymentState",
        });
      }
      headers.push({ text: "", value: "actions", sortable: false });
      return headers;
    },
    years() {
      const years = [];
      for (
        let currentYear = 2021;
        currentYear <= moment().year();
        ++currentYear
      ) {
        years.push(currentYear);
      }
      return years;
    },
    paymentStates() {
      return PaymentStates.map((state) => {
        return {
          value: state,
          text: this.$t(`paymentState.${state}`),
        };
      });
    },
    filteredUsers() {
      if (
        !this.filterYear ||
        !this.filterPaymentState ||
        this.selectedGroup !== "member"
      ) {
        return this.users;
      }
      return this.users.filter(
        (user) => this.getPaymentStateForUser(user) === this.filterPaymentState
      );
    },
  },
  watch: {
    selectedGroup: {
      immediate: true,
      handler() {
        this.loadUserData();
      },
    },
  },
  methods: {
    async loadUserData() {
      this.loading = true;
      this.users = (
        await usersGroupsResource.get(this.selectedGroup, {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        })
      ).data.map((user) => {
        user.fullName =
          user.attributes.memberType === "F"
            ? user.attributes.company
            : user.firstName
            ? `${user.firstName} ${user.lastName}`
            : "";
        return user;
      });
      this.loading = false;
    },
    showUserEditForm(user) {
      this.editUser = user;
      this.oldUser = JSON.stringify(user);
      this.editUserDialog = true;
    },
    showUserAddForm() {
      this.editUser = {
        attributes: {
          hasCompanyAddress: false,
          hasInvoiceAddress: false,
        },
      };
      this.addUserDialog = true;
    },
    showTransferUserForm(user) {
      this.editUser = user;
      this.newMembershipNumber = null;
      this.transferErrorMessage = null;
      this.transferUserDialog = true;
    },
    showUserDeleteConfirmationForm(user) {
      this.editUser = user;
      this.deleteUserDialog = true;
    },
    async importUserData() {
      this.importing = true;
      const formData = new FormData();
      formData.append("sendImportMails", this.sendImportMails);
      formData.append("userGroups", this.importGroups);
      formData.append("file", this.importFile);
      this.importResult = (
        await usersImportResource.post("", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        })
      ).data;
      this.importing = false;
      this.addUserDialog = false;
      this.confirmImportDialog = true;
    },
    async deleteUser() {
      await usersResource.delete(this.editUser.id, {
        headers: {
          "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
        },
      });
      this.users.splice(this.users.indexOf(this.editUser), 1);
      this.deleteUserDialog = false;
    },
    async transferUserData() {
      this.transferring = true;
      await usersResource
        .put(
          this.editUser.id,
          { enabled: true, username: this.newMembershipNumber },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        )
        .catch(() => {
          this.transferErrorMessage = this.$t("error.usernameExists", {
            username: this.newMembershipNumber,
          });
        });
      if (this.transferErrorMessage) {
        this.transferring = false;
        return;
      }
      await usersResource.put(`${this.editUser.id}/groups`, ["member"], {
        headers: {
          "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
        },
      });
      await usersResource.post(
        `${this.editUser.id}/register/mail`,
        { wileyMail: this.pageSettings.wileyMail },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
      this.transferring = false;
      this.transferUserDialog = false;
      this.loadUserData();
    },
    closeUserEditForm(resetValue) {
      if (resetValue) {
        const oldUser = JSON.parse(this.oldUser);
        this.$set(
          this.users,
          this.users.findIndex((user) => user.id === oldUser.id),
          oldUser
        );
      }
      this.editUserDialog = false;
    },
    closeUserAddForm(reload) {
      if (reload) {
        this.loadUserData();
      }
      this.addUserDialog = false;
    },
    closeImportConfirmationDialog() {
      this.loadUserData();
      this.confirmImportDialog = false;
    },
    removeImportGroup(item) {
      this.importGroups.splice(this.importGroups.indexOf(item), 1);
    },
    getPaymentStateForUser(user) {
      return user.attributes[`paymentState-${this.filterYear}`] || "notPaid";
    },
    switchSinglePaymentState(user) {
      this.selectedUsers = [user];
      this.switchPaymentStateDialog = true;
    },
    async doSwitchPaymentState() {
      this.switchingState = true;
      await switchPaymentStateResource.post(
        "",
        {
          ids: this.selectedUsers.map((user) => user.id),
          year: this.filterYear,
          state: this.switchPaymentState,
        },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
      this.switchingState = false;
      this.switchPaymentStateDialog = false;
      this.loadUserData();
    },
  },
};
</script>
